import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, User, X, Recycle, Facebook, Linkedin, Instagram, Youtube } from "lucide-react";
import { Logo } from "../Logo";
import { Button } from "@/components/ui/button";
import { PRIMARY_NAV, COMPLIANCE_MODE } from "@/data/site";
import { useAuth } from "@/hooks/useAuth";
import { trackPublicCta } from "@/lib/track";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TopUtilityBar = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginHref = user ? (isAdmin ? "/cws-admin" : "/dashboard") : "/login";
  const loginLabel = user ? "My Account" : "Login";
  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === "/login") {
      // Reset form by reloading the route
      navigate("/", { replace: true });
      setTimeout(() => navigate("/login", { replace: true }), 0);
    } else {
      navigate(loginHref);
    }
  };
  return (
    <div className="hidden border-b border-white/10 bg-[hsl(var(--primary-deep))] text-primary-foreground xl:block">
      <div className="container-cws flex h-8 items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70 whitespace-nowrap">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(120_60%_60%)] align-middle mr-2" />
          Serving Communities Since <span className="text-white">1992</span>
        </p>
        <nav className="flex items-center gap-0.5 whitespace-nowrap">
          <Link to="/oakland#oakland-customer-support" onClick={() => trackPublicCta("Oakland Support", { target_path: "/oakland#oakland-customer-support", city: "Oakland" })} className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">Oakland Support</Link>
          <span className="h-2.5 w-px bg-white/20" />
          <Link to="/san-jose#san-jose-customer-support" onClick={() => trackPublicCta("San José Support", { target_path: "/san-jose#san-jose-customer-support", city: "San José" })} className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">San José Support</Link>
          <span className="h-2.5 w-px bg-white/20" />
          <Link to="/commercial#quote" onClick={() => trackPublicCta("Commercial Quote", { target_path: "/commercial#quote" })} className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">Commercial Quote</Link>
          <span className="h-2.5 w-px bg-white/20" />
          <Link to="/pay-commercial-invoice" onClick={() => trackPublicCta("Pay Invoice", { target_path: "/pay-commercial-invoice" })} className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">Pay Invoice</Link>
          <span className="h-2.5 w-px bg-white/20" />
          <Link to="/billing" onClick={() => trackPublicCta("Billing Help", { target_path: "/billing" })} className="px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">Billing Help</Link>
          {!COMPLIANCE_MODE && <span className="h-2.5 w-px bg-white/20" />}
          {!COMPLIANCE_MODE && (
            <a href={loginHref} onClick={(e) => { trackPublicCta(user ? "My Account" : "Customer Login", { target_path: loginHref }); handleLoginClick(e); }} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 transition-colors hover:text-white">
              <User className="h-3 w-3" /> {loginLabel}
            </a>
          )}
        </nav>
      </div>
    </div>
  );
};

const DesktopNav = () => (
  <nav className="hidden xl:flex min-w-0 flex-1 items-center justify-center gap-0 2xl:gap-1 ml-4 2xl:ml-6">
    {PRIMARY_NAV.map((item) => (
      <div key={item.label} className="group relative">
        <NavLink
          to={item.href}
          end={item.href === "/"}
          className={({ isActive }) =>
            `relative inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 2xl:px-3 py-2 text-[13px] 2xl:text-[14px] font-medium transition-colors after:absolute after:left-2 after:right-2 2xl:after:left-3 2xl:after:right-3 after:bottom-1 after:h-[2px] after:rounded-full after:bg-gradient-to-r after:from-primary after:to-accent after:origin-left after:scale-x-0 after:transition-transform after:duration-300 hover:after:scale-x-100 ${
              isActive ? "text-primary after:scale-x-100" : "text-charcoal hover:text-primary"
            }`
          }
        >
          {item.label}
          {item.submenu && <ChevronDown className="h-3 w-3 opacity-60 transition-transform group-hover:rotate-180" />}
        </NavLink>
        {item.submenu && (
          <div className="invisible absolute left-0 top-full z-50 w-72 -translate-y-1 pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
            <div className="overflow-hidden rounded-md border border-border bg-white p-2 shadow-card">
              {item.submenu.map((sub) => (
                <Link
                  key={sub.label}
                  to={sub.href}
                  className="block rounded-md px-3 py-2 text-[13px] text-charcoal transition-colors hover:bg-primary-soft hover:text-primary"
                >
                  {sub.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    ))}
  </nav>
);

const MobileQuickAccess = () => {
  const { user, isAdmin } = useAuth();
  const loginHref = user ? (isAdmin ? "/cws-admin" : "/dashboard") : "/login";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Quick access"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-charcoal transition-colors hover:border-primary hover:text-primary"
        >
          <Recycle className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {!COMPLIANCE_MODE && <DropdownMenuItem asChild><Link to={loginHref}>Customer Login</Link></DropdownMenuItem>}
        <DropdownMenuItem asChild><Link to="/oakland#oakland-customer-support">Oakland Support</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/san-jose#san-jose-customer-support">San José Support</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/commercial#quote">Commercial Quote</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/pay-commercial-invoice">Pay Commercial Invoice</Link></DropdownMenuItem>
        
        <DropdownMenuItem asChild><Link to="/contact">Get Support</Link></DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileMenu = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] xl:hidden">
      <div className="absolute inset-0 bg-charcoal/50 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-[88%] max-w-sm flex-col bg-white shadow-2xl animate-slide-in-right">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <Logo size="sm" kind="primary" />
          <button aria-label="Close menu" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-md text-charcoal hover:bg-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          <Accordion type="single" collapsible className="w-full">
            {PRIMARY_NAV.map((item) => (
              <AccordionItem key={item.label} value={item.label} className="border-b border-border">
                {item.submenu ? (
                  <>
                    <AccordionTrigger className="px-3 py-3 text-[15px] font-semibold text-charcoal hover:no-underline">
                      {item.label}
                    </AccordionTrigger>
                    <AccordionContent className="pb-2">
                      <div className="flex flex-col">
                        <Link onClick={onClose} to={item.href} className="px-5 py-2 text-[13.5px] font-semibold text-primary">
                          Go to {item.label}
                        </Link>
                        {item.submenu.map((sub) => (
                          <Link key={sub.label} onClick={onClose} to={sub.href} className="px-5 py-2 text-[13.5px] text-muted-foreground hover:text-primary">
                            {sub.label}
                          </Link>
                        ))}
                      </div>
                    </AccordionContent>
                  </>
                ) : (
                  <Link onClick={onClose} to={item.href} className="block px-3 py-3 text-[15px] font-semibold text-charcoal hover:text-primary">
                    {item.label}
                  </Link>
                )}
              </AccordionItem>
            ))}
          </Accordion>

          <div className="px-3 py-5">
            <Button asChild variant="cta" size="lg" className="w-full" onClick={() => { trackPublicCta("Get Support", { target_path: "/contact" }); onClose(); }}>
              <Link to="/contact" id="cta-mobile-get-support" data-gtm="cta_click" data-gtm-id="get_support" data-gtm-location="mobile_menu" data-gtm-type="cta">Get Support</Link>
            </Button>
          </div>

          <div className="flex items-center justify-center gap-5 border-t border-border px-3 py-5">
            {[
              { Icon: Facebook, label: "Facebook", href: "https://www.facebook.com/calwastesolutions/" },
              { Icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/californiawastesolutions" },
              { Icon: Instagram, label: "Instagram", href: "https://www.instagram.com/californiawastesolutions/" },
              { Icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@CaliforniaWasteSolutions" },
            ].map(({ Icon, label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="text-muted-foreground transition-colors hover:text-primary">
                <Icon className="h-5 w-5" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);

  return (
    <header className="sticky top-0 z-50 w-full">
      <TopUtilityBar />
      <div className={`bg-white/95 backdrop-blur-md transition-shadow ${scrolled ? "shadow-header header-scrolled" : ""} border-b border-border`}>
        <div className="container-cws flex h-[70px] items-center justify-between gap-3 lg:h-[78px] xl:gap-4 2xl:gap-6">
          <Logo size="lg" kind="primary" />
          <DesktopNav />
          <div className="hidden xl:block shrink-0">
            <Button asChild variant="cta" size="default" className="whitespace-nowrap 2xl:h-12 2xl:px-6 2xl:text-[14.5px]" onClick={() => trackPublicCta("Get Support", { target_path: "/contact" })}>
              <Link to="/contact" id="cta-header-get-support" data-gtm="cta_click" data-gtm-id="get_support" data-gtm-location="header" data-gtm-type="cta">Get Support</Link>
            </Button>
          </div>
          <div className="flex items-center gap-2 xl:hidden">
            <MobileQuickAccess />
            <button
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-charcoal transition-colors hover:border-primary hover:text-primary"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
};
